import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useMarketLiveOdds } from "@/hooks/useMarketLiveOdds";
import { useMarketVolume } from "@/hooks/useMarketVolume";

interface PriceCardProps {
  market: {
    id: string;
    currentProbability: number;
    change24h: number;
    volume24h: number;
    liquidity: number;
  };
}

const PriceCard = ({ market }: PriceCardProps) => {
  // Get real-time probability data
  const { yesPrice, isLoading: oddsLoading, hasLiveData } = useMarketLiveOdds(market.id, market.currentProbability);
  
  // Get real trading volume
  const { totalVolume, isLoading: volumeLoading } = useMarketVolume(market.id);
  
  const isPositive = market.change24h >= 0;
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  // Use real data when available, fallback to market data
  const displayProbability = hasLiveData ? yesPrice : market.currentProbability;
  const displayVolume = totalVolume > 0 ? totalVolume : market.volume24h;

  return (
    <Card className="p-6 bg-card border-border">
      <div className="space-y-6">
        {/* Current Probability */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Current Probability</p>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-foreground">
              {oddsLoading ? (
                <div className="w-16 h-10 bg-muted animate-pulse rounded" />
              ) : (
                `${displayProbability.toFixed(1)}%`
              )}
            </span>
            <div className={`flex items-center gap-1 text-sm font-medium ${
              isPositive ? "text-success" : "text-destructive"
            }`}>
              {isPositive ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              {Math.abs(market.change24h)}%
            </div>
          </div>
          <p className="text-xs text-muted-foreground">24h change</p>
        </div>

        {/* Price Bar */}
        <div className="pt-6 border-t border-border">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-success font-medium">YES {displayProbability.toFixed(1)}%</span>
            <span className="text-destructive font-medium">NO {(100 - displayProbability).toFixed(1)}%</span>
          </div>
          <div className="h-3 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-success to-success/80 transition-all duration-500"
              style={{ width: `${displayProbability}%` }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PriceCard;
